import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import styled from 'styled-components';
import api from '../../../utils/authUtils';
import {
  Container, BackLink, Header, Title, Subtitle, Card, Button, Message,
  List, ListItem, Badge, Empty, Textarea, Field, Actions,
} from '../ui';
import { eventNameFor } from '../../../utils/eventName';
import OneTimeEmailDialog from '../../emailAdmin/OneTimeEmailDialog';

// The optional one-time edit beside Accept and Reject; the one-click buttons are unchanged.
const EditEmailLink = styled.button`
  display: inline-flex;
  align-items: center;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: .78rem;
  font-weight: 700;
  color: #0369a1;
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: #075985; }
`;

const STATUS_META = {
  SUBMITTED: { bg: '#e0f2fe', fg: '#075985', id: 'submissions.status.submitted', def: 'Submitted' },
  UNDER_REVIEW: { bg: '#fef3c7', fg: '#92400e', id: 'submissions.status.underReview', def: 'Under review' },
  ACCEPTED: { bg: '#dcfce7', fg: '#166534', id: 'submissions.status.accepted', def: 'Accepted' },
  REJECTED: { bg: '#fee2e2', fg: '#991b1b', id: 'submissions.status.rejected', def: 'Rejected' },
  WITHDRAWN: { bg: '#f1f5f9', fg: '#64748b', id: 'submissions.status.withdrawn', def: 'Withdrawn' },
};
const OPEN_STATUSES = ['SUBMITTED', 'UNDER_REVIEW'];

const TextBlock = ({ label, value }) => value ? (
  <div style={{ marginBottom: '.9rem' }}>
    <div style={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '.3rem' }}>{label}</div>
    <div style={{ fontSize: '.9rem', color: '#1f2937', whiteSpace: 'pre-wrap' }}>{value}</div>
  </div>
) : null;

// coAuthorEntries/heartsMatchIdeas are the structured fields new submissions
// use; coAuthors/heartsMatch (plain strings) only ever have content on a
// submission created before those existed - shown as a fallback so nothing
// from an old submission just disappears.
const CoAuthorsBlock = ({ entries, legacy }) => {
  if (entries && entries.length > 0) {
    return (
      <div style={{ marginBottom: '.9rem' }}>
        <div style={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '.3rem' }}>Co-authors</div>
        {entries.map((c, i) => (
          <div key={i} style={{ fontSize: '.9rem', color: '#1f2937' }}>{c.name} — {c.email}</div>
        ))}
      </div>
    );
  }
  return <TextBlock label="Co-authors" value={legacy} />;
};

const HeartsMatchBlock = ({ ideas, legacy, label }) => {
  if (ideas && ideas.length > 0) {
    return (
      <div style={{ marginBottom: '.9rem' }}>
        <div style={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '.3rem' }}>{label}</div>
        <ol style={{ margin: 0, paddingLeft: '1.1rem' }}>
          {ideas.map((idea, i) => (
            <li key={i} style={{ fontSize: '.9rem', color: '#1f2937' }}>{idea}</li>
          ))}
        </ol>
      </div>
    );
  }
  return <TextBlock label={label} value={legacy} />;
};

// Admin review of scientific submissions - open items surfaced here the same
// way Speaker Management surfaces pending title changes/slides: a banner at
// the top, filterable, one place to act on everything outstanding.
const SubmissionList = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [submissions, setSubmissions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [openOnly, setOpenOnly] = useState(false);
  // The submission and status whose decision email is being edited for this one send (null = closed).
  const [emailEdit, setEmailEdit] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/api/cms/submissions');
      setSubmissions(data || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else setMessage({ error: true, text: t('submissionMgmt.loadError', 'Could not load submissions.') });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCount = submissions.filter(s => OPEN_STATUSES.includes(s.status)).length;
  const shown = openOnly ? submissions.filter(s => OPEN_STATUSES.includes(s.status)) : submissions;

  const toggle = s => {
    if (expanded === s.id) { setExpanded(null); return; }
    setExpanded(s.id);
    setDrafts(d => ({ ...d, [s.id]: d[s.id] || { internalNotes: s.internalNotes || '', notesToSubmitter: s.notesToSubmitter || '' } }));
  };

  const setDraft = (id, key, value) => setDrafts(d => ({ ...d, [id]: { ...d[id], [key]: value } }));

  // The write both paths share: the plain buttons send no template, the dialog only what the admin
  // changed. The submitter note is part of the email ({{organizerNote}}), so the draft goes with it.
  const saveDecision = async (s, status, template = null) => {
    const draft = drafts[s.id] || {};
    await api.put(`/api/cms/submissions/${s.id}/status`, {
      status, internalNotes: draft.internalNotes || '', notesToSubmitter: draft.notesToSubmitter || '',
      ...(template ? { template } : {}),
    });
    await load();
    setMessage({ text: t('submissionMgmt.updated', 'Submission updated.') });
  };

  const decide = async (s, status) => {
    setBusy(true);
    setMessage(null);
    try {
      await saveDecision(s, status);
    } catch (err) {
      setMessage({ error: true, text: t('submissionMgmt.updateError', 'Could not update this submission.') });
    } finally {
      setBusy(false);
    }
  };

  // The preview the one-time dialog loads: the decision email for the status the button would send,
  // rendered with the note draft as it stands now. sendsEmail=false is the "no email for this
  // change" case the dialog shows (it is not reachable from these two buttons' states).
  const loadDecisionEmail = async (template) => {
    const draft = drafts[emailEdit.submission.id] || {};
    const { data } = await api.post(
      `/api/cms/submissions/${emailEdit.submission.id}/decision-email/preview`,
      {
        status: emailEdit.status,
        notesToSubmitter: draft.notesToSubmitter || '',
        ...(template ? { subject: template.subject, html: template.html, highlight: true } : {}),
      },
    );
    if (data.sendsEmail === false) {
      return { sendsEmail: false, subject: '', html: '', template: null, placeholders: [], templateProblems: [] };
    }
    return { ...data.email, sendsEmail: true, emailKey: data.emailKey };
  };

  return (
    <Container>
      <BackLink to="/admin/dashboard"><ArrowLeft size={16} /><FormattedMessage id="submissionMgmt.back" defaultMessage="Back to Dashboard" /></BackLink>
      <Header>
        <Title><FormattedMessage id="submissionMgmt.title" defaultMessage="Scientific Submissions" /></Title>
        <Button onClick={load}><RefreshCw size={16} /><FormattedMessage id="submissionMgmt.refresh" defaultMessage="Refresh" /></Button>
      </Header>
      <Subtitle><FormattedMessage id="submissionMgmt.subtitle" defaultMessage="Review scientific submissions from any registered user." /></Subtitle>
      {message && <Message error={message.error}>{message.text}</Message>}

      {openCount > 0 && (
        <Card style={{ borderLeft: '4px solid #92400e', background: '#fffbeb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#92400e" />
              <strong>
                <FormattedMessage id="submissionMgmt.banner"
                  defaultMessage="{count, plural, one {# submission is} other {# submissions are}} awaiting review."
                  values={{ count: openCount }} />
              </strong>
            </div>
            <Button onClick={() => setOpenOnly(o => !o)}>
              {openOnly
                ? <FormattedMessage id="submissionMgmt.showAll" defaultMessage="Show all" />
                : <FormattedMessage id="submissionMgmt.review" defaultMessage="Review now" />}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        {shown.length ? (
          <List>
            {shown.map(s => {
              const meta = STATUS_META[s.status] || { bg: '#f1f5f9', fg: '#334155', id: null, def: s.status };
              const isOpen = expanded === s.id;
              const draft = drafts[s.id] || { internalNotes: s.internalNotes || '', notesToSubmitter: s.notesToSubmitter || '' };
              const decidable = OPEN_STATUSES.includes(s.status);
              return (
                <ListItem key={s.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => toggle(s)}>
                    <div style={{ flex: 1, minWidth: '14rem' }}>
                      <div style={{ fontWeight: 700, color: '#111D4E' }}>{s.title}</div>
                      <div style={{ fontSize: '.82rem', color: '#64748b' }}>
                        {s.submitterName || s.submitterEmail} · {eventNameFor(intl.locale, s.eventName, s.eventNameEN)}
                      </div>
                    </div>
                    <Badge style={{ background: meta.bg, color: meta.fg }}>{meta.id ? t(meta.id, meta.def) : meta.def}</Badge>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>

                  {isOpen && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #eef2f7', paddingTop: '1rem' }}>
                      <CoAuthorsBlock entries={s.coAuthorEntries} legacy={s.coAuthors} />
                      <TextBlock label={t('submissions.field.phoneNumber', 'Phone number')} value={s.phoneNumber} />
                      <TextBlock label={t('submissionMgmt.field.language', 'Language')} value={s.language} />
                      <TextBlock label={t('submissions.field.background', 'Background')} value={s.backgroundText} />
                      <TextBlock label={t('submissions.field.methods', 'Methods')} value={s.methodsText} />
                      <TextBlock label={t('submissions.field.results', 'Results')} value={s.resultsText} />
                      <TextBlock label={t('submissions.field.conclusion', 'Conclusion')} value={s.conclusionText} />
                      <HeartsMatchBlock ideas={s.heartsMatchIdeas} legacy={s.heartsMatch} label={t('submissions.field.heartsMatch', 'HEARTS MATCH')} />

                      <Field>
                        {t('submissionMgmt.field.internalNotes', 'Internal notes (never shown to submitter)')}
                        <Textarea value={draft.internalNotes} onChange={e => setDraft(s.id, 'internalNotes', e.target.value)} />
                      </Field>
                      <Field style={{ marginTop: '.75rem' }}>
                        {t('submissionMgmt.field.notesToSubmitter', 'Note to submitter (sent with the decision)')}
                        <Textarea value={draft.notesToSubmitter} onChange={e => setDraft(s.id, 'notesToSubmitter', e.target.value)} />
                      </Field>

                      <Actions>
                        {decidable && s.status !== 'UNDER_REVIEW' && (
                          <Button secondary disabled={busy} onClick={() => decide(s, 'UNDER_REVIEW')}>
                            <FormattedMessage id="submissionMgmt.markUnderReview" defaultMessage="Mark under review" />
                          </Button>
                        )}
                        {decidable && (
                          <>
                            <Button disabled={busy} onClick={() => decide(s, 'ACCEPTED')}>
                              <FormattedMessage id="submissionMgmt.accept" defaultMessage="Accept" />
                            </Button>
                            <EditEmailLink type="button" disabled={busy} onClick={() => setEmailEdit({ submission: s, status: 'ACCEPTED' })}>
                              <FormattedMessage id="emailAdmin.oneTime.editFirst" defaultMessage="Edit email first…" />
                            </EditEmailLink>
                            <Button danger disabled={busy} onClick={() => decide(s, 'REJECTED')}>
                              <FormattedMessage id="submissionMgmt.reject" defaultMessage="Reject" />
                            </Button>
                            <EditEmailLink type="button" disabled={busy} onClick={() => setEmailEdit({ submission: s, status: 'REJECTED' })}>
                              <FormattedMessage id="emailAdmin.oneTime.editFirst" defaultMessage="Edit email first…" />
                            </EditEmailLink>
                          </>
                        )}
                        {!decidable && (
                          <Button secondary disabled={busy} onClick={() => decide(s, s.status)}>
                            <FormattedMessage id="submissionMgmt.saveNotes" defaultMessage="Save notes" />
                          </Button>
                        )}
                      </Actions>
                    </div>
                  )}
                </ListItem>
              );
            })}
          </List>
        ) : <Empty><FormattedMessage id="submissionMgmt.empty" defaultMessage="No submissions yet." /></Empty>}
      </Card>

      {emailEdit && (
        <OneTimeEmailDialog
          title={t('submissionMgmt.editEmail.title', 'Decision email for “{title}”', { title: emailEdit.submission.title })}
          intro={t('submissionMgmt.editEmail.intro',
            'This is the email the decision would send. Edit it for this send only, then save the decision.')}
          templateKey={emailEdit.status === 'ACCEPTED' ? 'SUBMISSION_DECISION_ACCEPTED' : 'SUBMISSION_DECISION_UPDATED'}
          load={loadDecisionEmail}
          confirmLabel={emailEdit.status === 'ACCEPTED'
            ? t('submissionMgmt.editEmail.accept', 'Accept and send')
            : t('submissionMgmt.editEmail.reject', 'Reject and send')}
          onConfirm={(template) => saveDecision(emailEdit.submission, emailEdit.status, template)}
          onClose={() => setEmailEdit(null)}
        />
      )}
    </Container>
  );
};

export default SubmissionList;
